import { Fragment, FunctionComponent, memo, useCallback, useContext, useRef, useState } from 'react'
import { Choice } from '@models/Survey'
import './VerticalChoices.scss'
import { ChoiceProps } from '../../AnswerProps'
import AnswerInput from '@components/AnswerInput/AnswerInput'
import { SurveyContext } from '@context/SurveyContext'
import chevronDownIcon from '@assets/images/icons/expand.png'
import chevronUpIcon from '@assets/images/icons/collapse.png'
import chevronDownActiveIcon from '@assets/images/icons/chevron-down-active.png'
import chevronUpActiveIcon from '@assets/images/icons/chevron-up-active.png'
import { AnswerType } from '@models/Survey/Answer'
import { SurveyStep } from '@models/Survey/SurveyStep'
import { TacticalQuestion } from '@models/Survey/Survey'
import { TacticalEntry } from '@models/Tactical'
import InfoIdleIcon from '@assets/images/icons/Info-idle.png'
import InfoHoverIcon from '@assets/images/icons/Info-hover.png'
import ModalDialog, { handleInfoUrl } from '@components/ModalDialog/ModalDialog'
import Markdown from '@components/Markdown/Markdown'
import { useTranslation } from 'react-i18next'
import { SurveyMode } from '@reducers/SurveyReducer'
import capitalize from 'lodash/capitalize'
import { useNavigate } from 'react-router-dom'
import { useShare } from '@hooks/useShare'

export interface OwnProps {
  className?: string
}

type Props = OwnProps & ChoiceProps

const VerticalChoices: FunctionComponent<Props> = ({ className, question, onSelection, onAnswer, answer }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('ModalDialog')
  const choiceRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const [infoIcon, setInfoIcon] = useState(InfoIdleIcon)
  const { currentQuestion, answerObject, step, mode } = useContext(SurveyContext)
  const [selectedQuestion, setSelectedQuestion] = useState(currentQuestion?.id)
  const [sandboxInfoDialogVisible, setSandboxInfoDialogVisible] = useState(false)
  const isSandbox = [SurveyMode.SANDBOX, SurveyMode.SANDBOX_PORTRAIT].includes(mode)

  const sandboxInfoText = currentQuestion?.meta?.[
    `sandbox${capitalize(language)}` as keyof typeof currentQuestion.meta
  ] as string | undefined

  const closeSandboxModal = useCallback(() => {
    setSandboxInfoDialogVisible(false)
  }, [setSandboxInfoDialogVisible])

  const showSandboxModal = useCallback(() => {
    if (sandboxInfoText) {
      setSandboxInfoDialogVisible(true)
    }
  }, [sandboxInfoText, setSandboxInfoDialogVisible])

  const { shareModalOpened } = useShare()

  const handleSandboxModalAction = useCallback(
    (url: string) => {
      closeSandboxModal()
      handleInfoUrl(url, navigate)
    },
    [closeSandboxModal, navigate],
  )

  const handleChangeIcon = (img: string) => {
    setInfoIcon(img)
  }

  const onClickedChoice = useCallback(
    (choice: Choice | AnswerType, type: 'question' | 'response'): void => {
      const answer = (currentQuestion as TacticalQuestion)?.meta?.tacticalReference
        ? ({
            value: (choice as Choice).id,
            reference: (currentQuestion as TacticalQuestion)?.meta?.tacticalReference,
          } as AnswerType)
        : (choice as Choice).id

      if (type === 'question') {
        onSelection(answer)
      } else {
        onAnswer?.(choice as AnswerType)
      }

      if (step === SurveyStep.Sandbox && type === 'question') {
        setSelectedQuestion((choice as Choice).id === selectedQuestion ? '' : (choice as Choice).id)
      }
    },
    [currentQuestion, onAnswer, onSelection, selectedQuestion, step],
  )

  return (
    <div className={`VerticalChoices ${className} ${shareModalOpened ? 'hide-choices' : ''}`}>
      <div className="choices">
        {question?.choices?.map((choice) => (
          <Fragment key={`ch_${choice.id}`}>
            <div
              ref={choiceRef}
              className={`VerticalChoices-item ${
                answer === choice.id || (answer as TacticalEntry)?.value === choice.id ? '-checked' : ''
              } item-${choice.id}`}
              key={choice.id}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                onClickedChoice(choice, 'question')
              }}>
              <input
                className="VerticalChoices-input -checked"
                id={`choice-${choice.id}`}
                type="radio"
                value={choice.id}
                checked={answer === choice.id}
                // to keep console happy
                onChange={() => {}}
              />
              <label className="VerticalChoices-label" htmlFor={`choice-${choice.id}`}>
                <span>{choice.text}</span>
                {step === SurveyStep.Sandbox && className === 'QuestionsBrowser' && (
                  <div className="VerticalChoices-chevron">
                    <img
                      className="VerticalChoices-toggle"
                      src={selectedQuestion === choice.id ? chevronUpIcon : chevronDownIcon}
                    />
                    <img
                      className="VerticalChoices-toggleUp"
                      src={selectedQuestion === choice.id ? chevronUpActiveIcon : chevronDownActiveIcon}
                    />
                  </div>
                )}
              </label>
            </div>

            {choice && currentQuestion && currentQuestion.id === choice.id && selectedQuestion === choice.id && (
              <div className="sub-choices">
                {sandboxInfoText && (
                  <div
                    className="VerticalChoices-info-with-icon"
                    onMouseOver={() => handleChangeIcon(InfoHoverIcon)}
                    onMouseLeave={() => handleChangeIcon(InfoIdleIcon)}
                    onClick={showSandboxModal}>
                    <img width="20px" src={infoIcon} />
                    <p className="VerticalChoices-info-with-icon-text">{t('action.moreInfoOnQuestion')}</p>
                  </div>
                )}

                <AnswerInput
                  question={currentQuestion}
                  answer={answerObject[answer as string]}
                  onSelection={(value) => onClickedChoice(value, 'response')}
                />
              </div>
            )}
          </Fragment>
        ))}
      </div>

      {isSandbox && sandboxInfoText && (
        <div key="md" className="TabbedPane-body-question-text">
          <ModalDialog
            bodyComponent={<Markdown content={sandboxInfoText} />}
            onClose={closeSandboxModal}
            visible={sandboxInfoDialogVisible}
            actionText={t('action.more')}
            action={() => {
              if (currentQuestion?.meta?.sandboxUrl) {
                handleSandboxModalAction(currentQuestion.meta.sandboxUrl)
              }
            }}
          />
        </div>
      )}
    </div>
  )
}

export default memo(VerticalChoices)
